import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { SCRIVE_TYPE } from '@treyd-io/core/constants/auth';
import { formatPhoneNumberWithLastFour } from '@treyd-io/core/utils/phone';
import { Col } from '@treyd-io/treyd-ui/components/Grid/Col';
import { Row } from '@treyd-io/treyd-ui/components/Grid/Row';
import IphoneIcon from '@treyd-io/treyd-ui/icons/IphoneIcon';
import { Form, Formik, FormikProps } from 'formik';
import { toString } from 'lodash';
import { useSession } from 'next-auth/react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { NoPhoneBody } from './NoPhoneBody';

export interface PhoneDigitsFormData {
  digits: string;
}

export const BANK_ID: Record<string, string> = {
  se: 'BankID',
  no: 'bankID',
  dk: 'MitID',
  fi: 'Your bank',
};
interface Props {
  onSubmit: (phone: string) => void;
  onSignWithBankClick?: () => void;
  showActions?: boolean;
  country: string;
  loading?: boolean;
  type: SCRIVE_TYPE;
}

export const PhoneBody = (props: Props) => {
  const { onSubmit, onSignWithBankClick, showActions, country, loading, type } =
    props;
  const { t } = useTranslation();
  const { data } = useSession({ required: true });
  const user = data?.user;
  const phoneNumber = toString(user?.phone_no);
  const phoneCode = toString(user?.phone_code);

  const VALIDATION_SCHEMA = yup.object({
    digits: yup
      .string()
      .required(t('last_four_digits_required'))
      .min(4, t('last_four_digits_required')),
  });

  const handlePhoneDigitsChange = (
    value: string,
    formik: FormikProps<PhoneDigitsFormData>
  ) => {
    const areDigits = /^(\s*|\d+)$/.test(value);
    if (areDigits) formik.setFieldValue('digits', value);
  };

  const handlePhoneDigitsSubmit = (values: PhoneDigitsFormData) => {
    const phone = formatPhoneNumberWithLastFour({
      phoneCode,
      phoneNumber,
      digits: values.digits,
    });
    onSubmit(phone);
  };

  if (!phoneCode || !phoneNumber) return <NoPhoneBody />;

  return (
    <>
      <Formik
        initialValues={{
          digits: '',
        }}
        onSubmit={handlePhoneDigitsSubmit}
        validationSchema={VALIDATION_SCHEMA}
        validateOnMount>
        {(formik) => {
          return (
            <Form noValidate>
              <DialogContent>
                <Typography color="text.secondary" variant="body1">
                  {type === SCRIVE_TYPE.SIGN &&
                    t('phone_sign_document_description')}
                  {type === SCRIVE_TYPE.AUTH && t('phone_auth_description')}
                </Typography>
                <Box marginTop={3}>
                  <Row>
                    <Col xs={12} sm>
                      <TextField
                        fullWidth
                        name="digits"
                        label={t('phone_number')}
                        value={formik.values.digits}
                        onBlur={formik.handleBlur}
                        onChange={(e) =>
                          handlePhoneDigitsChange(e.target.value, formik)
                        }
                        error={
                          formik.touched.digits && Boolean(formik.errors.digits)
                        }
                        helperText={
                          formik.touched.digits && formik.errors.digits
                        }
                        placeholder={t('last_four_digits')}
                        inputProps={{ maxLength: 4 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment
                              position="start"
                              sx={{ color: 'text.primary' }}>
                              <IphoneIcon />+{phoneCode}-
                              {phoneNumber?.slice(0, -4)}
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Col>
                    <Col xs={12} sm="auto">
                      <Button
                        fullWidth
                        size="large"
                        disabled={!formik.isValid || loading}
                        type="submit"
                        variant="contained">
                        {t('send_verification_code')}
                      </Button>
                    </Col>
                  </Row>
                </Box>
              </DialogContent>
              <DialogActions
                sx={{
                  display: { sm: 'inline-flex' },
                }}>
                {showActions && onSignWithBankClick && (
                  <Button
                    type="button"
                    color="info"
                    onClick={onSignWithBankClick}>
                    {type === SCRIVE_TYPE.SIGN &&
                      t('sign_with_bank_id_instead', {
                        bankId: BANK_ID[country],
                      })}
                    {type === SCRIVE_TYPE.AUTH &&
                      t('confirm_with_bank_id_instead', {
                        bankId: BANK_ID[country],
                      })}
                  </Button>
                )}
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};
